import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import useEquipmentValidation from "../../../hooks/useEquipmentValidation";
import useUserStore from "../../../stores/user";
import Checkbox from "../../Checkbox/Index";
import Select from "../../form/select";
import MultiSelect from "../../form/select/multi";
import StepperInput from "../../form/stepper-input";
import TextInput from "../../form/text-input";
import Button from "../../new-button";
import SlidingTab from "../../SlidingTab/SlidingTab";
import {
  CABLE_LENGTH_OPTIONS,
  CONNECTIVITY_OPTIONS,
  FILE_TYPE_OPTIONS,
  PACKAGE_OPTIONS,
  PACKAGE_PRICING_MAPPING,
  PURCHASE_TYPE_MAPPINGS,
} from "./constants";
import {
  getButtonText,
  getEnabledPurchaseOptions,
  getPurchaseType,
  getTPPCategoryKey,
} from "./helpers";
import styles from "./styles.module.scss";

const EquipmentConfigurator = ({
  equipment,
  allEquipments,
  allSelectedEquipments,
  handleAddConfiguredEquipment,
  onEquipmentChange,
  buttonClassName = "",
  containerClassName = "",
}) => {
  const defaultValues = useRef({});
  const { validateEquipment } = useEquipmentValidation();
  const userOrgIsPaychex = useUserStore((state) => state.userOrgIsPaychex);
  const userOrgIsEMS = useUserStore((state) => state.userOrgIsEMS);
  const [errorFields, setErrorFields] = useState(new Set());
  const equipmentType = equipment.equipment_type;
  const isNotAGateway = equipmentType !== "gateway";
  const isPaysleyMobileTerminal = equipment?.id == 19;
  const changeAddonsLabelToPackage =
    isPaysleyMobileTerminal && userOrgIsPaychex;
  const renderAddonsFirst = isPaysleyMobileTerminal && userOrgIsEMS;

  const shouldShowTPP = useMemo(() => {
    const selectedTPPEquipments = allSelectedEquipments.filter(
      (e) =>
        e.type === "apply_tpp" &&
        getTPPCategoryKey(e.category_id) ===
          getTPPCategoryKey(equipment.category_id),
    );

    return (
      (selectedTPPEquipments?.length === 0 ||
        equipment.type === "apply_tpp") &&
      !!equipment.apply_tpp
    );
  }, [allSelectedEquipments, equipment]);

  const options = useMemo(() => {
    const enabledOptions = getEnabledPurchaseOptions(equipment);

    if (shouldShowTPP) {
      return enabledOptions;
    }

    return enabledOptions.filter((option) => option !== "apply_tpp");
  }, [equipment, shouldShowTPP]);

  const [purchaseType, setPurchaseType] = useState(() => {
    return options.length > 0 ? options[0] : null;
  });
  const [quantity, setQuantity] = useState(1);
  const shouldDisableQuantityIncrease = purchaseType === "apply_tpp";

  useEffect(() => {
    const defaults = {};
    Object.keys(PURCHASE_TYPE_MAPPINGS)
      .map((e) => `${e}_default`)
      .forEach((field) => {
        if (field in equipment) {
          defaults[field] = equipment[field] || "";
        }
      });
    defaultValues.current = defaults;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (errorFields.has(name)) {
      const newErrorFields = new Set(errorFields);
      newErrorFields.delete(name);
      setErrorFields(newErrorFields);
    }

    if (name === "connectivity_default") {
      const connectivity = value?.value ?? value;
      if (connectivity !== "sim") {
        const next = new Set(errorFields);
        next.delete("sim_provided_by");
        next.delete("sim_card_monthly_default");
        setErrorFields(next);
      }
    }

    onEquipmentChange({
      ...equipment,
      [name]: value,
    });
  };

  const handleAddonChange = (selectedOptions) => {
    const updatedEquipment = {
      selected_addons: selectedOptions.map((opt) => opt.value),
    };

    if (
      selectedOptions.length > 0 &&
      equipment?.dynamic_monthly_fee_default &&
      Object.prototype.hasOwnProperty.call(
        equipment?.dynamic_monthly_fee_default,
        selectedOptions[0].value,
      )
    ) {
      updatedEquipment.monthly_fee_default =
        equipment?.dynamic_monthly_fee_default[selectedOptions[0].value];
    }

    const newErrorFields = new Set(errorFields);
    Object.keys(updatedEquipment).forEach((field) => {
      if (newErrorFields.has(field)) {
        newErrorFields.delete(field);
      }
    });
    setErrorFields(newErrorFields);

    onEquipmentChange({
      ...equipment,
      ...updatedEquipment,
    });
  };

  const handlePurchaseTypeChange = (val) => {
    const newPurchaseType = getPurchaseType(val);
    const oldDefaultFieldName = `${purchaseType}_default`;

    const updatedEquipment = {
      ...equipment,
      [oldDefaultFieldName]: defaultValues.current[oldDefaultFieldName] || "",
    };

    setPurchaseType(newPurchaseType);
    onEquipmentChange(updatedEquipment);
  };

  useEffect(() => {
    if (options.length > 0 && !purchaseType) {
      setPurchaseType(options[0]);
    }
  }, [options, purchaseType]);

  const handleSubmit = () => {
    const { hasErrors, errorFields: newErrorFields } =
      validateEquipment(equipment);

    setErrorFields(newErrorFields);

    if (hasErrors) {
      toast.error("Please complete all required equipment fields", {
        duration: 5000,
      });
      return;
    }

    handleAddConfiguredEquipment(purchaseType ?? "monthly_fee", quantity);
  };

  return (
    <div className={`${styles.configurationContainer} ${containerClassName}`}>
      {!!equipment?.add_on_equipment && renderAddonsFirst && (
        <MultiSelect
          placeholder={
            changeAddonsLabelToPackage ? `Select Package` : `Select Add-ons`
          }
          id="selected_addons"
          name="selected_addons"
          value={
            equipment?.selected_addons?.map((id) => ({
              value: id.toString(),
              label:
                equipment?.add_on_equipment_list.find((e) => e.id == id)
                  ?.equipment_name || id,
            })) || []
          }
          options={equipment?.add_on_equipment_list.map((e) => ({
            value: e.id.toString(),
            label: e.equipment_name,
          }))}
          onChange={handleAddonChange}
          dropdownHeader={
            changeAddonsLabelToPackage ? `Select package` : `Select add-ons`
          }
          showSelectAll
          selectedTextPrefix={
            changeAddonsLabelToPackage ? `Packages: ` : `Add-ons: `
          }
        />
      )}

      {!!equipment?.sass_fee && (
        <TextInput
          label={"SaaS Fee"}
          placeholder={"0.00"}
          type="number"
          inputMode="decimal"
          prefix={"$"}
          suffix={"/mo"}
          id="sass_fee_default"
          name="sass_fee_default"
          value={equipment?.sass_fee_default || ""}
          onChange={handleChange}
          error={errorFields.has("sass_fee_default")}
        />
      )}

      {!!equipment?.per_transaction && (
        <TextInput
          label={"Per Transaction Fee"}
          placeholder={"0.00"}
          type="number"
          inputMode="decimal"
          prefix={"$"}
          suffix={"/txn"}
          id="per_transaction_default"
          name="per_transaction_default"
          value={equipment?.per_transaction_default || ""}
          onChange={handleChange}
          error={errorFields.has("per_transaction_default")}
        />
      )}

      {!!equipment?.amount && (
        <TextInput
          label={"Amount"}
          placeholder={"0.00"}
          type="number"
          inputMode="decimal"
          prefix={"$"}
          id="amount_default"
          name="amount_default"
          value={equipment?.amount_default || ""}
          onChange={handleChange}
          error={errorFields.has("amount_default")}
        />
      )}

      {!!equipment?.factor_rate && (
        <TextInput
          label={"Factor Rate"}
          placeholder={"0.00"}
          type="number"
          inputMode="decimal"
          suffix={"%"}
          id="factor_rate_default"
          name="factor_rate_default"
          value={equipment?.factor_rate_default || ""}
          onChange={handleChange}
          error={errorFields.has("factor_rate_default")}
        />
      )}

      {!!equipment?.payback && (
        <TextInput
          label={"Payback"}
          placeholder={"0"}
          type="number"
          inputMode="decimal"
          suffix={" months"}
          id="payback_default"
          name="payback_default"
          value={equipment?.payback_default || ""}
          onChange={handleChange}
          error={errorFields.has("payback_default")}
        />
      )}

      {!!equipment?.monthly_wireless && (
        <TextInput
          label={"Monthly Wireless"}
          placeholder={"0.00"}
          type="number"
          inputMode="decimal"
          prefix={"$"}
          suffix={"/mo"}
          id="monthly_wireless_default"
          name="monthly_wireless_default"
          value={equipment?.monthly_wireless_default || ""}
          onChange={handleChange}
          error={errorFields.has("monthly_wireless_default")}
        />
      )}

      {!!equipment?.text_sms_fee && (
        <TextInput
          label={"Text/SMS Fee"}
          placeholder={"0.00"}
          type="number"
          inputMode="decimal"
          prefix={"$"}
          id="text_sms_fee_default"
          name="text_sms_fee_default"
          value={equipment?.text_sms_fee_default || ""}
          onChange={handleChange}
          error={errorFields.has("text_sms_fee_default")}
        />
      )}

      {!!equipment?.sim_card && (
        <TextInput
          label={"SIM Card Monthly"}
          placeholder={"0.00"}
          type="number"
          inputMode="decimal"
          prefix={"$"}
          suffix={"/mo"}
          id="sim_card_monthly_default"
          name="sim_card_monthly_default"
          value={equipment?.sim_card_monthly_default || ""}
          onChange={handleChange}
          error={errorFields.has("sim_card_monthly_default")}
          errored={errorFields.has("sim_card_monthly_default")}
          touched={errorFields.has("sim_card_monthly_default")}
        />
      )}

      {/* Custom fields that need to be handled separately */}
      {!!equipment?.file_types && (
        <Select
          placeholder="File types"
          dropdownHeader="File types"
          options={equipment?.file_types_default?.split(",").map((type) => ({
            value: type,
            label: FILE_TYPE_OPTIONS[type],
          }))}
          value={equipment?.file_type_key || ""}
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "file_type_key",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {!!equipment?.hardware && (
        <Select
          placeholder="Hardware"
          dropdownHeader="Hardware"
          options={allEquipments
            .filter((e) =>
              equipment.hardware_default.split(",").includes(e.id.toString()),
            )
            .map((hardware) => ({
              value: hardware.id,
              label: hardware.equipment_name,
            }))}
          value={equipment?.hardware_id || ""}
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "hardware_id",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {!!equipment?.package && (
        <Select
          placeholder="Package"
          dropdownHeader="Package"
          options={Object.entries(PACKAGE_OPTIONS)
            .filter(([key]) =>
              equipment.package_default.split(",").includes(key),
            )
            .map(([key, value]) => ({
              value: key,
              label: value,
            }))}
          value={equipment?.package_key || ""}
          onChange={(selectedOption) => {
            handleChange({
              target: {
                name: "package_key",
                value: selectedOption,
              },
            });

            if (selectedOption && PACKAGE_PRICING_MAPPING[selectedOption]) {
              handleChange({
                target: {
                  name: "monthly_fee_default",
                  value: PACKAGE_PRICING_MAPPING[selectedOption],
                },
              });
            }
          }}
          errored={errorFields.has("package_key")}
          error={errorFields.has("package_key")}
          touched={errorFields.has("package_key")}
        />
      )}

      {!!equipment?.other_equipments && (
        <Select
          placeholder="Other Equipment"
          dropdownHeader="Other Equipment"
          options={(equipment?.other_equipments_list || []).map((other) => ({
            value: other.id,
            label: other.equipment_name,
          }))}
          value={equipment?.other_equipment_id || ""}
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "other_equipment_id",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {!!equipment?.pin_debit && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            label="Add PIN Debit"
            id="pin_debit_default"
            name="pin_debit_default"
            checked={equipment?.pin_debit_default === "yes" ? true : false}
            onChange={(e) => {
              handleChange({
                target: {
                  name: "pin_debit_default",
                  value: e.target.checked ? "yes" : "no",
                },
              });
            }}
          />
          <label htmlFor="pin_debit_default">Add PIN Debit</label>
        </div>
      )}

      {!!equipment?.cable_length && (
        <Select
          placeholder="Cable Length"
          dropdownHeader="Cable Length"
          options={Object.entries(CABLE_LENGTH_OPTIONS).map(([key, value]) => ({
            value: key,
            label: value,
          }))}
          value={equipment?.cable_length_key || ""}
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "cable_length_key",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {!!equipment?.connectivity && (
        <Select
          placeholder="Connectivity"
          dropdownHeader="Connectivity"
          options={equipment?.connectivity_options?.split(",").map((type) => ({
            value: type,
            label: CONNECTIVITY_OPTIONS[type],
          }))}
          value={equipment?.connectivity_default || ""}
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "connectivity_default",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {equipment?.connectivity_default === "sim" && (
        <>
          <Select
            placeholder="SIM Provided By"
            dropdownHeader="SIM Provided By"
            options={[
              {
                value: "processor_provided",
                label: "Processor Provided",
              },
              {
                value: "merchant_provided",
                label: "Merchant Provided",
              },
            ]}
            value={equipment?.sim_provided_by || ""}
            onChange={(selectedOption) =>
              handleChange({
                target: { name: "sim_provided_by", value: selectedOption },
              })
            }
            error={errorFields.has("sim_provided_by")}
            errored={errorFields.has("sim_provided_by")}
            touched={errorFields.has("sim_provided_by")}
          />

          {equipment?.sim_provided_by === "processor_provided" && (
            <>
              <TextInput
                label={"SIM Card Monthly"}
                placeholder={"0.00"}
                type="number"
                inputMode="decimal"
                prefix={"$"}
                suffix={"/mo"}
                id="sim_card_monthly_default"
                name="sim_card_monthly_default"
                value={equipment?.sim_card_monthly_default || ""}
                onChange={handleChange}
                error={errorFields.has("sim_card_monthly_default")}
                errored={errorFields.has("sim_card_monthly_default")}
                touched={errorFields.has("sim_card_monthly_default")}
              />

              {!!equipment?.monthly_cellular_txn && (
                <TextInput
                  label={"Monthly Cellular Transactions"}
                  placeholder={"0"}
                  type="number"
                  inputMode="numeric"
                  id="monthly_cellular_txn_default"
                  name="monthly_cellular_txn_default"
                  value={equipment?.monthly_cellular_txn_default || ""}
                  onChange={handleChange}
                  error={errorFields.has("monthly_cellular_txn_default")}
                />
              )}

              {!!equipment?.cellular_per_txn && (
                <TextInput
                  label={"Cellular Per Transaction"}
                  placeholder={"0.00"}
                  type="number"
                  inputMode="decimal"
                  prefix={"$"}
                  suffix={"/txn"}
                  id="cellular_per_txn_default"
                  name="cellular_per_txn_default"
                  value={equipment?.cellular_per_txn_default || ""}
                  onChange={handleChange}
                  error={errorFields.has("cellular_per_txn_default")}
                />
              )}
            </>
          )}
        </>
      )}

      {!!equipment?.pin_pad && (
        <TextInput
          label="PIN Pad Name"
          placeholder="PIN Pad Name"
          type="text"
          id="pin_pad_name_default"
          name="pin_pad_name_default"
          value={equipment?.pin_pad_name_default || ""}
          onChange={handleChange}
          error={errorFields.has("pin_pad_name_default")}
          errored={errorFields.has("pin_pad_name_default")}
          touched={errorFields.has("pin_pad_name_default")}
        />
      )}

      {!!equipment?.customer_facing && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            label="Customer Facing"
            id="customer_facing_default"
            name="customer_facing_default"
            checked={
              equipment?.customer_facing_default === "yes" ? true : false
            }
            onChange={(e) => {
              handleChange({
                target: {
                  name: "customer_facing_default",
                  value: e.target.checked ? "yes" : "no",
                },
              });
            }}
          />
          <label htmlFor="customer_facing_default">Customer Facing</label>
        </div>
      )}

      {!!equipment?.virtual_terminal && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            label="Virtual Terminal"
            id="virtual_terminal_default"
            name="virtual_terminal_default"
            checked={
              equipment?.virtual_terminal_default === "yes" ? true : false
            }
            onChange={(e) => {
              handleChange({
                target: {
                  name: "virtual_terminal_default",
                  value: e.target.checked ? "yes" : "no",
                },
              });
            }}
          />
          <label htmlFor="virtual_terminal_default">Virtual Terminal</label>
        </div>
      )}

      {!!equipment?.monthly_fee && !options.includes("monthly_fee") && (
        <TextInput
          label={"Monthly Fee"}
          placeholder={"0.00"}
          type="number"
          inputMode="decimal"
          prefix={"$"}
          suffix={"/mo"}
          id="monthly_fee_default"
          name="monthly_fee_default"
          value={equipment?.monthly_fee_default || ""}
          onChange={handleChange}
          error={errorFields.has("monthly_fee_default")}
        />
      )}

      {!!equipment?.add_on_equipment && !renderAddonsFirst && (
        <MultiSelect
          placeholder={
            changeAddonsLabelToPackage ? `Select Package` : `Select Add-ons`
          }
          id="selected_addons"
          name="selected_addons"
          value={
            equipment?.selected_addons?.map((id) => ({
              value: id.toString(),
              label:
                equipment?.add_on_equipment_list.find((e) => e.id == id)
                  ?.equipment_name || id,
            })) || []
          }
          options={equipment?.add_on_equipment_list.map((e) => ({
            value: e.id.toString(),
            label: e.equipment_name,
          }))}
          onChange={handleAddonChange}
          dropdownHeader={
            changeAddonsLabelToPackage ? `Select package` : `Select add-ons`
          }
          showSelectAll
          selectedTextPrefix={
            changeAddonsLabelToPackage ? `Packages: ` : `Add-ons: `
          }
        />
      )}
      {options?.length >= 1 && (
        <>
          {options.length > 1 && (
            <SlidingTab
              options={options.map((option) => getButtonText(option))}
              selected={getButtonText(purchaseType)}
              onChange={handlePurchaseTypeChange}
            />
          )}

          {equipment &&
            purchaseType &&
            !!equipment?.[purchaseType] &&
            purchaseType !== "apply_tpp" && (
              <TextInput
                key={`${equipment?.id}-${purchaseType}`}
                label={
                  options.length === 1
                    ? purchaseType
                        ?.replace(/_/g, " ")
                        ?.replace(/\b\w/g, (l) => l.toUpperCase())
                    : ""
                }
                placeholder={"0.00"}
                type="text"
                inputMode="decimal"
                prefix={"$"}
                suffix={
                  purchaseType === "rent_for" ||
                  purchaseType === "lease_for" ||
                  purchaseType === "monthly_fee"
                    ? "/mo"
                    : undefined
                }
                id={`${purchaseType}_default`}
                name={`${purchaseType}_default`}
                value={equipment?.[`${purchaseType}_default`] || ""}
                readOnly={purchaseType === "purchase_for" && userOrgIsPaychex}
                error={errorFields.has(`${purchaseType}_default`)}
                cleaveOptions={{
                  numeral: true,
                  numeralDecimalScale: 2,
                  numeralIntegerScale: 6,
                  numeralDecimalMark: ".",
                  numeralPositiveOnly: true,
                  onValueChanged: (e) => {
                    handleChange({
                      target: {
                        name: `${purchaseType}_default`,
                        value: e.target.rawValue,
                      },
                    });
                  },
                }}
              />
            )}
        </>
      )}

      {isNotAGateway && (
        <StepperInput
          name="quantity"
          min={1}
          value={1}
          id={`quantity-eqp`}
          onChange={(value) => {
            setQuantity(value);
          }}
          disableQuantityIncrease={shouldDisableQuantityIncrease}
        />
      )}

      <div className={`${styles.buttonContainer} ${buttonClassName}`}>
        <Button
          type="button"
          onClick={handleSubmit}
          fullWidth
        >
          {isNotAGateway ? getButtonText(purchaseType) : "Select"}
        </Button>
      </div>
    </div>
  );
};

export default EquipmentConfigurator;
